import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Target, Zap, TrendingUp, Users, Crown, Star, Award, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ConfettiEffect } from '@/components/celebrations/ConfettiEffect';

export interface AwardedAchievement {
  playerId: string;
  achievementId: string;
  achievementName: string;
  isNew: boolean;
  timesAchieved: number;
}

interface AchievementCelebrationContextType {
  showAchievementCelebration: (achievements: AwardedAchievement[], currentUserId: string) => void;
}

const AchievementCelebrationContext = createContext<AchievementCelebrationContextType | null>(null);

export const useAchievementCelebration = () => {
  const context = useContext(AchievementCelebrationContext);
  if (!context) {
    throw new Error('useAchievementCelebration must be used within AchievementCelebrationProvider');
  }
  return context;
};

const getAchievementIcon = (name: string) => {
  const nameLower = name.toLowerCase();
  if (nameLower.includes('perfect')) return Target;
  if (nameLower.includes('streak')) return Zap;
  if (nameLower.includes('rating') || nameLower.includes('climber')) return TrendingUp;
  if (nameLower.includes('social') || nameLower.includes('butterfly')) return Users;
  if (nameLower.includes('tournament') || nameLower.includes('champion')) return Crown;
  if (nameLower.includes('first') || nameLower.includes('win')) return Trophy;
  return Award;
};

const getAchievementMessage = (name: string, timesAchieved: number, isNew: boolean): string => {
  const nameLower = name.toLowerCase();
  
  if (nameLower.includes('perfect')) {
    return isNew 
      ? '💯 Flawless! You won a set 6-0!' 
      : `💯 Dominant! That's ${timesAchieved} perfect sets now!`;
  }
  if (nameLower.includes('streak')) {
    return isNew
      ? '🔥 Unstoppable! 5 consecutive wins!'
      : `🔥 On fire! Streak achievement earned ${timesAchieved} times!`;
  }
  if (nameLower.includes('first')) {
    return '🎉 Your first victory on the court!';
  }
  
  return isNew 
    ? '🌟 New achievement unlocked!' 
    : `🌟 Achievement earned ${timesAchieved} times!`;
};

interface AchievementCelebrationProviderProps {
  children: ReactNode;
}

export const AchievementCelebrationProvider: React.FC<AchievementCelebrationProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [currentAchievements, setCurrentAchievements] = useState<AwardedAchievement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const showAchievementCelebration = useCallback((achievements: AwardedAchievement[], currentUserId: string) => {
    // Filter to only show achievements for the current user
    const userAchievements = achievements.filter(a => a.playerId === currentUserId);
    
    if (userAchievements.length === 0) return;
    
    setCurrentAchievements(userAchievements);
    setCurrentIndex(0);
    setOpen(true);
  }, []);

  const handleNext = () => {
    if (currentIndex < currentAchievements.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setOpen(false);
      setCurrentAchievements([]);
      setCurrentIndex(0);
    }
  };

  const current = currentAchievements[currentIndex];
  const Icon = current ? getAchievementIcon(current.achievementName) : Trophy;

  return (
    <AchievementCelebrationContext.Provider value={{ showAchievementCelebration }}>
      {children}
      
      <Dialog open={open} onOpenChange={(isOpen) => {
        if (!isOpen) {
          setOpen(false);
          setCurrentAchievements([]);
          setCurrentIndex(0);
        }
      }}>
        <DialogContent className="sm:max-w-md overflow-hidden">
          <DialogTitle className="sr-only">Achievement Unlocked</DialogTitle>
          
          {open && <ConfettiEffect isActive={true} />}
          
          <AnimatePresence mode="wait">
            {current && (
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="flex flex-col items-center text-center py-6"
              >
                {/* Achievement Icon */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
                  className="relative mb-6"
                >
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/30">
                    <Icon className="h-12 w-12 text-primary-foreground" />
                  </div>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                    className="absolute -top-2 -right-2"
                  >
                    <Sparkles className="h-8 w-8 text-yellow-500" />
                  </motion.div>
                </motion.div>

                {/* Title */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-2"
                >
                  <h2 className="text-2xl font-bold text-foreground">
                    {current.isNew ? 'Achievement Unlocked!' : 'Achievement Earned!'}
                  </h2>
                  
                  <div className="flex items-center justify-center gap-2">
                    <Star className="h-5 w-5 text-primary" />
                    <span className="text-xl font-semibold text-primary">
                      {current.achievementName}
                    </span>
                    <Star className="h-5 w-5 text-primary" />
                  </div>
                  
                  {current.timesAchieved > 1 && (
                    <Badge variant="secondary" className="text-sm">
                      ×{current.timesAchieved} times
                    </Badge>
                  )}
                </motion.div>

                {/* Message */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-4 text-muted-foreground max-w-xs"
                >
                  {getAchievementMessage(current.achievementName, current.timesAchieved, current.isNew)}
                </motion.p>

                {/* Progress indicator for multiple achievements */}
                {currentAchievements.length > 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="flex gap-2 mt-4"
                  >
                    {currentAchievements.map((_, i) => (
                      <div
                        key={i}
                        className={`w-2 h-2 rounded-full ${i === currentIndex ? 'bg-primary' : 'bg-muted'}`}
                      />
                    ))}
                  </motion.div>
                )}

                {/* Button */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-6 w-full"
                >
                  <Button onClick={handleNext} className="w-full">
                    {currentIndex < currentAchievements.length - 1 ? 'Next Achievement' : 'Awesome!'}
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </AchievementCelebrationContext.Provider>
  );
};
